import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CreditCard, Mail, Trash2, Download, AlertTriangle } from "lucide-react";

export default function AdminPaymentLogs() {
  const { t } = useI18n();
  const [payments, setPayments] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterMonths, setFilterMonths] = useState("");
  const [showCleanup, setShowCleanup] = useState(null);
  const [cleanupMonths, setCleanupMonths] = useState("6");

  const fetchData = () => {
    const params = filterMonths ? `?months=${filterMonths}` : "";
    Promise.all([
      api.get(`/admin/payment-logs${params}`),
      api.get(`/admin/email-logs${params}`),
    ]).then(([p, e]) => { setPayments(p.data); setEmails(e.data); })
      .catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [filterMonths]);

  const handleCleanup = async (type) => {
    try {
      const endpoint = type === "payments" ? "/admin/payment-logs/cleanup" : "/admin/email-logs/cleanup";
      const res = await api.delete(endpoint, { data: { keep_months: parseInt(cleanupMonths) } });
      toast.success(`${res.data.deleted} records verwijderd`);
      setShowCleanup(null);
      fetchData();
    } catch { toast.error("Fout bij opschonen"); }
  };

  const exportLogs = (type) => {
    const data = type === "payments" ? payments : emails;
    const headers = type === "payments" ? "Order ID;Gebruiker;Plan;Bedrag;Status;Datum" : "Aan;Onderwerp;Template;Status;Datum";
    const rows = data.map(d => type === "payments"
      ? `${d.order_id || ""};${d.user?.email || ""};${d.plan_name || ""};${d.total || ""};${d.status};${d.created_at?.slice(0,19)}`
      : `${d.to};${d.subject || ""};${d.template_slug || ""};${d.status};${d.created_at?.slice(0,19)}`
    );
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${type}_logs_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    toast.success("CSV geexporteerd");
  };

  const statusColor = (s) => {
    if (["completed", "paid", "sent"].includes(s)) return "bg-emerald-500/20 text-emerald-400 border-0";
    if (["pending", "queued", "mock_sent"].includes(s)) return "bg-amber-500/20 text-amber-400 border-0";
    return "bg-red-500/20 text-red-400 border-0";
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-payment-logs-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Payment & Email Logs</h1>
        <div className="flex items-center gap-2">
          <Select value={filterMonths || "all"} onValueChange={(v) => setFilterMonths(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 h-8"><SelectValue placeholder="Filter periode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alles</SelectItem>
              <SelectItem value="1">Laatste maand</SelectItem>
              <SelectItem value="3">Laatste 3 maanden</SelectItem>
              <SelectItem value="6">Laatste 6 maanden</SelectItem>
              <SelectItem value="12">Laatste jaar</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="payments">
        <TabsList>
          <TabsTrigger value="payments"><CreditCard className="w-3.5 h-3.5 mr-1.5" />Betalingen ({payments.length})</TabsTrigger>
          <TabsTrigger value="emails"><Mail className="w-3.5 h-3.5 mr-1.5" />E-mails ({emails.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="mt-4">
          <div className="flex justify-end gap-2 mb-3">
            <Button variant="outline" size="sm" onClick={() => exportLogs("payments")}><Download className="w-3.5 h-3.5 mr-1.5" />Excel Export</Button>
            <Button variant="outline" size="sm" className="text-destructive border-destructive/30" onClick={() => setShowCleanup("payments")}><Trash2 className="w-3.5 h-3.5 mr-1.5" />Opschonen</Button>
          </div>
          <Card className="bg-card border-border overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Order ID</TableHead><TableHead>Gebruiker</TableHead><TableHead>Plan</TableHead>
                <TableHead className="text-right">Bedrag</TableHead><TableHead>Status</TableHead><TableHead>Datum</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {payments.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Geen betalingen</TableCell></TableRow> :
                payments.map((p, i) => (
                  <TableRow key={i} className="table-row-hover">
                    <TableCell className="font-mono-data text-sm">{p.order_id}</TableCell>
                    <TableCell className="text-sm">{p.user?.email || ""}</TableCell>
                    <TableCell>{p.plan_name} <span className="text-xs text-muted-foreground capitalize">({p.frequency})</span></TableCell>
                    <TableCell className="text-right font-mono-data">{p.total?.toFixed(2)} EUR</TableCell>
                    <TableCell><Badge className={statusColor(p.status)}>{p.status}</Badge></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleString("nl-NL") : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="emails" className="mt-4">
          <div className="flex justify-end gap-2 mb-3">
            <Button variant="outline" size="sm" onClick={() => exportLogs("emails")}><Download className="w-3.5 h-3.5 mr-1.5" />Excel Export</Button>
            <Button variant="outline" size="sm" className="text-destructive border-destructive/30" onClick={() => setShowCleanup("emails")}><Trash2 className="w-3.5 h-3.5 mr-1.5" />Opschonen</Button>
          </div>
          <Card className="bg-card border-border overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Aan</TableHead><TableHead>Onderwerp</TableHead><TableHead>Template</TableHead>
                <TableHead>Status</TableHead><TableHead>Datum</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {emails.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Geen e-mails</TableCell></TableRow> :
                emails.map((e, i) => (
                  <TableRow key={i} className="table-row-hover">
                    <TableCell className="text-sm">{e.to}</TableCell>
                    <TableCell className="text-sm">{e.subject}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{e.template_slug || "-"}</Badge></TableCell>
                    <TableCell>
                      <Badge className={statusColor(e.status)}>{e.status}</Badge>
                      {e.error && <div className="text-xs text-red-400 mt-1 max-w-[220px] break-words">{e.error}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(e.sent_at || e.created_at).toLocaleString("nl-NL")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Cleanup dialog */}
      {showCleanup && (
        <Dialog open={!!showCleanup} onOpenChange={() => setShowCleanup(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Logs Opschonen</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">Verwijder oude {showCleanup === "payments" ? "betalings" : "e-mail"} logs. Exporteer eerst naar Excel als je de data wilt bewaren.</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm">Behoud laatste</span>
              <Select value={cleanupMonths} onValueChange={setCleanupMonths}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 maand</SelectItem>
                  <SelectItem value="3">3 maanden</SelectItem>
                  <SelectItem value="6">6 maanden</SelectItem>
                  <SelectItem value="12">12 maanden</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCleanup(null)}>Annuleren</Button>
              <Button variant="destructive" onClick={() => handleCleanup(showCleanup)}>Opschonen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
